import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useToast } from "../toast/ToastProvider";
import Footer from "../Footer";
import Topbar from "./Navigations/Topbar";
import { getCartProductsByCustomerId } from "../../service/CartService";
import { getWishlistProductsByCustomerId } from "../../service/WishlistService";
import { apiUrl } from "../../config";
import { formatINR } from "../../App";
import { fetchOrdersByCustomerId } from "../../service/OrderService";
import OrderLine from "./OrderLine";
import { fetchCustomerProductById } from "../../service/ProductService";

const OrderConfirmed = () => {
  const params = useParams();

  const { orderId, itemId } = params;

  const navigate = useNavigate();
  const toast = useToast();

  const authUser = useSelector((state) => state.auth.authUser);

  const [order, setOrder] = useState(null);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [productData, setProductData] = useState([]);

  // Fetch Cart & Wishlist
  useEffect(() => {
    if (!authUser?.id) return;

    const fetchCart = async () => {
      try {
        const cartData = await getCartProductsByCustomerId(authUser.id);
        setCart(cartData || []);
      } catch (error) {
        console.error("Cart fetch failed", error);
      }
    };

    const fetchWishlist = async () => {
      try {
        const wish = await getWishlistProductsByCustomerId(authUser.id);
        setWishlist(wish || []);
      } catch (error) {
        console.error("Wishlist fetch failed", error);
      }
    };

    fetchCart();
    fetchWishlist();
  }, [authUser?.id]);

  useEffect(() => {
    if (!authUser?.id) return;

    const loadOrder = async () => {
      try {
        const orderItems = await fetchOrdersByCustomerId(authUser.id);

        const found = orderItems?.find(
          (item) =>
            item.orderId === Number(orderId) && item.itemId === Number(itemId),
        );
        if (found) {
          setOrder(found);          
          const product = await fetchCustomerProductById(found.productId);
          setProductData(product);
        } else {
          toast("Order not found", "ERROR");
          navigate("/my-orders");
        }
      } catch (error) {
        toast("Failed to load order details", "ERROR");
        navigate("/my-orders");
      }
    };

    loadOrder();
  }, [orderId, itemId, authUser?.id]);

  if (!authUser?.id || !order) {
    return (
      <div className="text-center py-5">
        <span className="spinner-border text-primary" role="status"></span>
        <p className="mt-3 fs-4">Loading order details...</p>
      </div>
    );
  }

  const makingPercent = Math.round((order.makingCharge*100)/order.productRate);
  const wastagePercent = Math.round((order.wastageCharge*100)/order.productRate);
  const productDiscount = order.discount;
  const productBeforeGst = (order.makingCharge+order.wastageCharge+order.productRate);
  const gstPercent = Math.round((order.gst * 100)/productBeforeGst);
  const discountPercent = Math.round((productDiscount * 100)/productBeforeGst);
  const productAfterGst = (order.makingCharge+order.wastageCharge+order.productRate+order.gst);
  const discountPrice = Math.round(productAfterGst-productDiscount);

  const getPurityValue = (purity) => {
    if (purity === "22k") return "91.6";
    if (purity === "24k") return "999.9";
    return purity;
  };  
  return (
    <>
      <header className="header style7">
        <Topbar />
      </header>

      <div className="container py-5">
        <Link to="/my-orders" className="btn btn-outline-secondary mb-4 fs-4">
          ← Back to Orders
        </Link>

        <div className="order-details-card bg-white rounded-4 shadow-sm p-4 p-md-5">
          <div className="product-section d-flex flex-column flex-md-row gap-4">
            <div className="product-image-container">
              <img
                src={
                  order.productImageUrl
                    ? `${apiUrl}/${order.productImageUrl.replace(/\\/g, "/")}`
                    : "/images/placeholder-product.jpg"
                }
                alt={order.productName}
                className="product-image-large rounded-3"
              />
            </div>

            <div className="product-info-section flex-grow-1">
              <h2
                className="product-title fw-bold mb-3"
                style={{ textTransform: "capitalize" }}
              >
                {order.productName}
              </h2>

              <div className="product-meta-grid">
                <div className="meta-item mb-3">
                  <label className="text-muted small d-block">Order ID</label>
                  <span className="order-id-text fw-semibold">
                    {order.orderId}
                  </span>
                </div>
                <div className="meta-item mb-3">
                  <label className="text-muted small d-block">Quantity</label>
                  <span className="fw-semibold">{order.productQuantity}</span>
                </div>
                <div className="meta-item mb-3">
                  <label className="text-muted small d-block">Price</label>
                  <span className="price-highlight text-success fw-bold">
                    {formatINR(Math.round(order.itemTotalAmount))}
                  </span>
                </div>
                <div className="meta-item mb-3">
                  <label className="text-muted small d-block">Status</label>
                  <span
                    className={`status-badge px-3 py-2 text-white ${
                      (order.status || "").toLowerCase() === "delivered"
                        ? "bg-success"
                        : (order.status || "").toLowerCase() === "packed"
                          ? "bg-secondary"
                          : (order.status || "").toLowerCase() === "shipped"
                            ? "bg-info"
                            : (order.status || "").toLowerCase() === "cancelled"
                              ? "bg-danger"
                              : "bg-warning text-dark"
                    }`}
                  >
                    {order.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="price-breakup-table-wrappers">
              <h5 className="price-breakup-titles">Price Breakup</h5>
              <table className="price-breakup-tables">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Rate</th>
                    <th>Weight</th>
                    <th>Final Value</th>
                  </tr>
                </thead>

                <tbody>
                  {/* Gold */}
                  <tr className="price-row">
                    <td>
                      <b>Gold ({getPurityValue(productData.purity)})</b>
                    </td>
                    <td>{formatINR(order.goldRate)}</td>
                    <td>{order.weight} g</td>
                    <td>{formatINR(Math.round(order.productRate))}</td>
                  </tr>

                  {/* Making */}
                  <tr className="price-row">
                    <td>Making Charges ({makingPercent}%)</td>
                    <td></td>
                    <td>-</td>
                    <td>{formatINR(Math.round(order.makingCharge))}</td>
                  </tr>

                  {/* Wastage */}
                  <tr className="price-row">
                    <td colSpan="3">Wastage ({wastagePercent}%)</td>
                    <td>{formatINR(Math.round(order.wastageCharge))}</td>
                  </tr>

                  {/* Total */}
                  <tr className="price-total">
                    <td colSpan="3">Total</td>
                    <td>{formatINR(Math.round(productBeforeGst))}</td>
                  </tr>

                  {/* Discount */}
                  {order.discount > 0 && (
                    <tr className="price-row">
                      <td colSpan="3">Discount ({discountPercent}%)</td>
                      <td>
                        {formatINR(
                          Math.round(productDiscount),
                        )}
                      </td>
                    </tr>
                  )}

                  {/* GST */}
                  {order.gst > 0 && (
                    <tr className="price-row">
                      <td colSpan="3">GST ({gstPercent}%)</td>
                      <td>{formatINR(Math.round(order.gst))}</td>
                    </tr>
                  )}

                  {/* Grand Total */}
                  <tr className="price-grand-total">
                    <td colSpan="3">Grand Total</td>
                    <td>{formatINR(discountPrice)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="timeline-section-page mt-5 pt-4 border-top">
            <h3 className="section-heading fw-bold mb-4">
              <i className="ph ph-map-trifold me-2"></i>
              Order Tracking
            </h3>
            <OrderLine selectedItem={order} />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderConfirmed;
